import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines } from "@/components/Reveal";
import { PrimaryCTA, SoftCTA } from "@/components/CTA";
import { Arrow } from "@/components/Annotation";
import { FigureStanding, StageMap } from "@/components/Sketches";
import { Tape, StickyNote, PunchHoles, RehearsalHeader, SectionLabel } from "@/components/Notebook";
import { LINKS, ROUTES } from "@/config/links";
import { useLang } from "@/i18n";

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const layerY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative min-h-[100svh] pt-28 md:pt-32 pb-16 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Left — kinetic headline (no scroll-fade: content, incl. the secondary CTA, must stay fully legible while scrolling) */}
        <motion.div className="lg:col-span-7 relative z-10 min-w-0">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="flex items-center gap-3 mb-6 min-w-0"
          >
            <span className="h-[1px] w-8 shrink-0 bg-terracotta" />
            <span className="font-body text-[11px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.28em] text-ink-soft break-words">
              {t("landing.hero_eyebrow")}
            </span>
          </motion.div>

          <h1 className="font-serif text-charcoal leading-[1.05] sm:leading-[0.95] text-[8vw] sm:text-6xl md:text-7xl lg:text-[5.5rem] tracking-tight break-words">
            <MaskedLines lines={[t("landing.hero_h1")]} delay={0} />
          </h1>

          {/* Description — desktop/laptop copy */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="hidden md:block mt-6 sm:mt-8 max-w-xl font-body text-sm sm:text-base md:text-lg text-ink-soft leading-[1.6]"
            data-testid="hero-description-desktop"
          >
            {t("landing.hero_description")}
          </motion.p>
          {/* Description — shorter mobile copy */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="md:hidden mt-6 max-w-xl font-body text-sm text-ink-soft leading-[1.6]"
            data-testid="hero-description-mobile"
          >
            {t("landing.hero_description_mobile")}
          </motion.p>

          {/* Service note — mobile shows it before the CTA, desktop shows it after the microcopy (see below) */}
          <p className="md:hidden mt-4 font-body text-sm text-ink-soft" data-testid="hero-service-note-mobile">
            {t("landing.hero_service_note")}
          </p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.7 }}
            className="mt-8 sm:mt-10 flex flex-col items-start gap-4"
          >
            <PrimaryCTA href={ROUTES.bookPresentation} testId="hero-book-cta" location="hero" targetAssetId="booking_presentation">
              {t("landing.hero_book_cta")}
            </PrimaryCTA>

            <p className="hidden md:block font-body text-sm text-ink-soft max-w-sm" data-testid="hero-microcopy-desktop">
              {t("landing.hero_microcopy")}
            </p>
            <p className="md:hidden font-body text-sm text-ink-soft" data-testid="hero-microcopy-mobile">
              {t("landing.hero_microcopy_mobile")}
            </p>

            <p className="hidden md:block font-body text-sm text-ink-soft" data-testid="hero-service-note-desktop">
              {t("landing.hero_service_note")}
            </p>

            <div className="pt-1">
              <SoftCTA href={LINKS.diagnostic} testId="hero-diagnostic-cta" location="hero" tone="light" targetAssetId="next_presentation_readiness">
                {t("landing.hero_diagnostic_cta")}
              </SoftCTA>
              <p className="hidden md:block mt-2 font-body text-sm text-ink-soft" data-testid="hero-diagnostic-helper">
                {t("landing.hero_diagnostic_helper")}
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* Right — the director's notebook page (all hand-drawn) */}
        <div className="lg:col-span-5 relative mt-16 lg:mt-0">
          <motion.div
            style={{ y: layerY }}
            initial={{ opacity: 0, y: 30, rotate: 1.2 }}
            animate={{ opacity: 1, y: 0, rotate: 1.2 }}
            transition={{ delay: 0.25, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative bg-paper border border-ink/12 shadow-[6px_12px_44px_rgba(27,27,24,0.12)] p-6 md:p-7 pl-12 md:pl-14"
          >
            <PunchHoles className="absolute left-4 top-7 bottom-7" count={7} />
            <Tape className="absolute -top-4 left-1/3" rotate={-6} width={130} />

            <RehearsalHeader n="01" className="mb-5" />

            <div className="grid grid-cols-5 gap-5 md:gap-6 items-center">
              <div className="col-span-2 text-ink flex items-center justify-center">
                <FigureStanding className="w-full h-auto max-h-[200px]" />
              </div>
              <div className="col-span-3 text-ink">
                <SectionLabel className="mb-3">STAGE MAP</SectionLabel>
                <StageMap className="w-full h-auto" />
              </div>
            </div>

            <div className="mt-4 border-t border-dashed border-ink/25 pt-3 flex items-center justify-between">
              <span className="font-hand text-ink-soft text-base md:text-lg">{t("landing.hero_build")}</span>
              <span className="font-body text-[10px] uppercase tracking-[0.2em] text-pencil">Take 01</span>
            </div>
          </motion.div>

          <StickyNote className="absolute -bottom-10 -left-2 md:-left-6 w-36 md:w-40 z-20" rotate={-5} color="#E7DDC8">
            {t("landing.hero_sticky")}
          </StickyNote>

          <Arrow className="hidden xl:block absolute -left-16 top-1/3 w-16 h-16" delay={0.7} />
        </div>
      </div>

      {/* scroll cue */}
      <motion.div style={{ opacity: fade }} className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <span className="font-body text-[10px] uppercase tracking-[0.24em] text-pencil">Turn the page</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="block w-[1px] h-8 bg-pencil"
        />
      </motion.div>
    </section>
  );
};
